import { Component, ViewChild, Inject, Optional, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ButtonModule } from 'primeng/button';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { BaseTableComponent } from 'src/app/layout/base-table-component';
import { ConfigMode } from 'src/app/models/constants';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { FiscalYear, MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { TableTemplateComponent } from 'src/app/templates/table-template/table-template.component';
import { ActionButtonEvent, ColumnType, InputType, TableColumnModel } from 'src/app/templates/table-template/tableModel';

type SummaryConfig = {
  configMode: ConfigMode,
  tableData: any[],
  aggregationKey: string,
  multiSortMeta: { field: string, order: number }[],
  columnList: (TableColumnModel & 
    { aggregate?: 'Avg' | 'WeightedAvg' | 'Sum', weightingField?: string })[]
};

@Component({
  selector: 'app-configuration',
  imports: [TableTemplateComponent, MatInputModule, MatSelectModule, FormsModule, MatButtonModule,
    ButtonModule, MatIconModule, MatProgressSpinnerModule
  ],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent extends BaseTableComponent {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(TableTemplateComponent) tableComponent!: TableTemplateComponent;
  @ViewChild('fySelect') fiscalYearSelect!: MatSelect;
  ConfigMode = ConfigMode;
  configMode: ConfigMode = ConfigMode.SHOP;
  //Map to hold updated items, with Map we can update existing item as well as know size like array length without converting to array.
  itemsToSave = new Map<string, any>();
  fiscalYears: FiscalYear[] = [];
  currentFiscalYear!: FiscalYear;
  selectedFiscalYear!: FiscalYear;
  showHeaderPanel = true;
  showSubmitButtons = true;
  editableTable = true;
  showSummaryButtons = false;
  shouldRefreshShop = false;
  updateConfigurationMethod: Function = async() => Promise<any>;
  title: string;
  parentShop: any;
  currentLineData: { shop_id: number, lineItems: any[] };
  loader = {
    isLoading: false,
    message: 'Loading Configuration...',
    hideTable: false
  };
  errorMessage = '';
  readonly COMPANY_NAME = "Company Name";
  readonly pageDescriptions = {
    shop: 'Maintain shop details and calculator inputs in one place. Inline edits stay local until you save, so teams can review updates before they affect downstream calculations.',
    line: 'Refine line-level values that roll up into the selected shop configuration. Keep the calculator accurate without leaving the current workflow.',
    summary: 'Review the rolled-up configuration values for the selected fiscal year. This view is read-only and intended for quick comparison across the organization.'
  };
  aggregationFields: {
    name: string, aggregate: 'Avg' | 'WeightedAvg' | 'Sum', weightingField?: string, label: string,
    fieldToSave: string, floatDigits?: number, valuePrefix?: string, value?: number, hidden?: boolean
  }[] = [];
  //summarize shop data into plants (one level aggregation)
  plantSummary: SummaryConfig = {
    configMode: ConfigMode.PLANT_SUMMARY,
    tableData: [],
    aggregationKey: 'plant_code',
    multiSortMeta: [
      { field: 'company_code', order: 1 },
      { field: 'plant_name', order: 1 }
    ],
    columnList: [
      { name: "company_code", label: this.COMPANY_NAME, type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "plant_name", label: "Plant Name", type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "team_members", aggregate: 'Sum', label: "Plant Team Members", type: ColumnType.numeric, matchMode: 'gt', colSize: 'long' },
      { name: "cost_per_minute", aggregate: 'WeightedAvg', weightingField: 'team_members', type: ColumnType.numeric,
        label: "Plant Average $ / Minute", matchMode: 'gt', colSize: 'small', currency: 'USD' },
      { name: "downtime_cost", aggregate: 'Sum', label: "Plant Downtime Cost", type: ColumnType.numeric, matchMode: 'gt', colSize: 'small', currency: 'USD' }
    ]
  };
  //summarize shop data into companies (two level aggregation)
  companySummary: SummaryConfig = {
    configMode: ConfigMode.COMPANY_SUMMARY,
    tableData: [],
    aggregationKey: 'company_code',
    multiSortMeta: [{ field: 'company_code', order: 1 }],
    columnList: [
      { name: "company_code", label: this.COMPANY_NAME, type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "team_members", aggregate: 'Sum', label: "Company Team Members", type: ColumnType.numeric, matchMode: 'gt', colSize: 'long' },
      { name: "cost_per_minute", aggregate: 'WeightedAvg', weightingField: 'team_members', type: ColumnType.numeric,
        label: "Company Average $ / Minute", matchMode: 'gt', colSize: 'small', currency: 'USD' },
      { name: "downtime_cost", aggregate: 'Sum', label: "Company Downtime Cost", type: ColumnType.numeric, matchMode: 'gt', colSize: 'small', currency: 'USD' }
    ]
  }

  constructor(router: Router, route: ActivatedRoute, private readonly mfgappsApiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService, private readonly dialog: MatDialog,
    @Optional() private readonly dialogRef: MatDialogRef<ConfigurationComponent>, 
    @Optional() @Inject(MAT_DIALOG_DATA) public readonly dialogData: any) {
    super(router, route);
    if (dialogData) {
      this.eventService.deepCopyObject(dialogData, this);
    }
  }

  async ngOnInit() {
    const fiscalYearData = this.eventService.getFiscalYears();
    this.fiscalYears = fiscalYearData.fiscalYears.slice();
    this.currentFiscalYear = fiscalYearData.currentFiscalYear;
    if (this.dialogData?.selectedFiscalYear) {
      this.selectedFiscalYear = this.fiscalYears.find(fy => fy.year === this.dialogData.selectedFiscalYear.year);
    }
    else {
      this.selectedFiscalYear = this.currentFiscalYear;
    }
    if (this.configMode === ConfigMode.SHOP) {
      this.initializeShopConfig();
    }
    else if (this.configMode === ConfigMode.LINE) {
      this.initializeLineConfig();
    }
    //Load configuration from API only when not opened as plant or company summary
    if (!this.dialogData?.isSummary) {
      await this.loadConfiguration(this.selectedFiscalYear.year);
    }
  }

  async onFiscalYearChange(event: any) {
    const previousSelectedfiscalYear = this.selectedFiscalYear;
    if (!this.editableTable || this.itemsToSave.size === 0 
      || await this.eventService.confirmDialog('All unsaved configuration changes will be lost. Do you wish to proceed?')) {
      this.selectedFiscalYear = event.value;
      //Allow edit only for current or future Fiscal year.
      this.editableTable = this.selectedFiscalYear.year >= this.currentFiscalYear.year;
      await this.loadConfiguration(this.selectedFiscalYear.year);
    }
    else {
      //Reset to previously selected fiscal year if user cancels on confirmation dialog
      this.fiscalYearSelect.value = previousSelectedfiscalYear;
    }
  }

  initializeShopConfig() {
    this.columnList = [
      { name: "company_code", label: this.COMPANY_NAME, type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "plant_name", label: "Plant Name", type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "shop_name", label: "Shop Name", type: ColumnType.category, matchMode: 'in', colSize: 'long' },
      { name: "plant_impact_minutes", isInteger: true, label: "Plant Impact (Minutes)",
        type: ColumnType.numeric, inputType: InputType.numeric, matchMode: 'gt', colSize: 'small', 
        required: true, checkPositive: true, hideFilter: true },
      { name: "company_impact_minutes", isInteger: true, label: "Company Impact (Minutes)",
        type: ColumnType.numeric, inputType: InputType.numeric, matchMode: 'gt', colSize: 'small',
        required: true, checkPositive: true, hideFilter: true },
      { name: "team_members", label: "Shop Team Members", type: ColumnType.numeric, matchMode: 'gt', 
        required: true, checkPositive: true, isInteger: true, colSize: 'small', hideFilter: true },
      { name: "cost_per_minute", label: "$ / Minute", type: ColumnType.numeric, matchMode: 'gt', 
        required: true, checkPositive: true, colSize: 'small', currency: 'USD', hideFilter: true },
      { name: "downtime_cost", label: "Shop Downtime Cost", type: ColumnType.numeric, matchMode: 'gt', 
        required: true, checkPositive: true, colSize: 'small', currency: 'USD', hideFilter: true },
      { name: "takt_time_seconds", label: "Shop Takt Time (Seconds)", type: ColumnType.numeric, matchMode: 'gt',
        required: true, checkPositive: true, isInteger: true, colSize: 'small', hideFilter: true },
      { name: "Config Line", label: "Actions", type: ColumnType.button, hideFilter: true, disableSort: true, colSize: 'mid' }
    ];
    this.multiSortMeta = [
      { field: 'company_code', order: 1 },
      { field: 'plant_name', order: 1 },
      { field: 'shop_name', order: 1 }
    ];
    this.showSummaryButtons = true;
    this.updateConfigurationMethod = this.updateShopConfiguration;
  }

  initializeLineConfig() {
    this.columnList = [
      { name: "line_name", label: "Line Name", type: ColumnType.text, matchMode: 'contains' },
      { name: "team_members", label: "Team Members", type: ColumnType.numeric, isInteger: true,
        inputType: InputType.numeric, required: true, checkPositive: true, matchMode: 'gt' },
      { name: "cost_per_minute", label: "$ / Minute", type: ColumnType.numeric, matchMode: 'gt',
        inputType: InputType.numeric, required: true, checkPositive: true, currency: 'USD' },
      { name: "downtime_cost", label: "Downtime Cost", type: ColumnType.numeric, matchMode: 'gt', 
        inputType: InputType.numeric, required: true, checkPositive: true, currency: 'USD' }
    ];
    this.aggregationFields.push(
      { name: 'team_members', fieldToSave: 'total_team_members', aggregate: 'Sum', 
        label: 'Team Members (Total)' },
      { name: 'cost_per_minute', fieldToSave: 'average_cost_per_minute', aggregate: 'WeightedAvg',
        label: '$ / Minute (Avg)', weightingField: 'team_members', valuePrefix: '$' },
      { name: 'downtime_cost', fieldToSave: 'total_downtime_cost', aggregate: 'Sum',
        label: 'Downtime Cost (Total)', valuePrefix: '$' },
      { name: 'takt_time_seconds', fieldToSave: 'average_takt_time_seconds', aggregate: 'Avg',
        label: 'Takt Time (Seconds) (Avg)', hidden: true }
    );
    const shopTaktTimeField = this.aggregationFields.find(field => field.name === 'takt_time_seconds');
    //Show Takt Time column only for Assembly type shops
    if (this.eventService.checkIfShopHasVehicleAvoidance(this.parentShop)) {
      this.columnList.push({
        name: "takt_time_seconds", label: "Takt Time (Seconds)", type: ColumnType.numeric, isInteger: true,
        inputType: InputType.numeric, required: true, checkPositive: true, matchMode: 'gt'
      });
      shopTaktTimeField.hidden = false;
    }
    this.multiSortMeta = [
      { field: 'line_name', order: 1 }
    ];
    this.updateConfigurationMethod = this.updateLineConfiguration;
  }

  async loadConfiguration(fiscalYear: number) {
    try {
      this.tableData = [];
      this.itemsToSave.clear();
      this.resetError();
      this.setLoader(true);
      let response: any;
      if (this.configMode === ConfigMode.SHOP) {
        response = await this.mfgappsApiService.getShopConfiguration(fiscalYear);
      }
      else if (this.configMode === ConfigMode.LINE) {
        response = await this.mfgappsApiService.getLineConfiguration(fiscalYear, this.parentShop.shop_id);
      }
      if (Array.isArray(response['result'])) {
        this.tableData = response['result'];
      }
      if (this.configMode === ConfigMode.SHOP) {
        this.calculateSummary();
      }
      else if (this.configMode === ConfigMode.LINE) {
        this.calculateLineAggregates();
      }
      //update URL query params on data load only when dialogData is null (not opened as dialog)
      if (!this.dialogData) {
        this.manageRouteParameters();
      }
      this.setLoader();
    }
    catch (error) {
      this.handleApiError(error, 'Loading', true);
    }
  }

  protected override rewriteRouteQueryParams(selectedPage: number, rowsPerPage: number): void {
    //update URL query params on pagination change only when dialogData is null (not opened as dialog)
    if (!this.dialogData) {
      super.rewriteRouteQueryParams(selectedPage, rowsPerPage);
    }
  }

  calculateSummary() {
    const plantMap = {};
    const companyMap = {};
    for (const [index, shop] of this.tableData.entries()) {
      this.aggregateShopInSummary(shop, this.plantSummary, plantMap, index === this.tableData.length - 1);
      this.aggregateShopInSummary(shop, this.companySummary, companyMap, index === this.tableData.length - 1);
    }
    this.plantSummary.tableData = Object.values(plantMap);
    this.companySummary.tableData = Object.values(companyMap);
  }

  aggregateShopInSummary(shop: any, summaryConfig: SummaryConfig, summaryMap: any, computeAverage = false) {
    let summaryObj = summaryMap[shop[summaryConfig.aggregationKey]];
    if (!summaryObj) {
      //Initialize plant/company object with count 0 and column value 0 if column type is aggregate
      summaryObj = summaryMap[shop[summaryConfig.aggregationKey]] = { count: 0 };
      for (const column of summaryConfig.columnList) {
        if (column.aggregate === 'WeightedAvg' && column.weightingField) {
          summaryObj[column.name] = { weightedSum: 0, weightedProduct: 0 };
        }
        else {
          summaryObj[column.name] = column.aggregate ? 0 : shop[column.name];
        }
      }
    }
    //Increment count to calculate average after processing last item of array
    summaryObj.count += 1;
    for (const column of summaryConfig.columnList) {
      if (column.aggregate === 'WeightedAvg' && column.weightingField) {
        //Sum the product of column value and weighting field value for weighted average calculation
        summaryObj[column.name].weightedProduct += shop[column.name] * shop[column.weightingField];
        summaryObj[column.name].weightedSum += shop[column.weightingField];
      }
      else if (column.aggregate) {
        summaryObj[column.name] += shop[column.name];
      }
      //Calculate average and weighted average for all summary object values if last index of array
      if (computeAverage && column.aggregate?.includes('Avg')) {
        for (const loopSummaryObj of Object.values(summaryMap)) {
          if (column.aggregate === 'Avg') {
            loopSummaryObj[column.name] = loopSummaryObj[column.name] / loopSummaryObj['count'];
          }
          else if (column.aggregate === 'WeightedAvg' && column.weightingField) {
            const loopColumnObj = loopSummaryObj[column.name];
            loopSummaryObj[column.name] = 0;
            if (loopColumnObj.weightedSum > 0) {
              loopSummaryObj[column.name] = loopColumnObj.weightedProduct / loopColumnObj.weightedSum;
            }
          }
        }
      }
    }
  }

  openSummary(payload: any) {
    payload.editableTable = payload.showSubmitButtons = payload.showHeaderPanel = false;
    payload.isSummary = true;
    payload.title = `${payload.configMode} for Fiscal Year : ${this.selectedFiscalYear.displayName}`;
    this.dialog.open(ConfigurationComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      height: '100%',
      width: '100%',
      data: payload
    });
  }

  validateItem(configItem: any) {
    const validationresult = { isValid: true, message: '' };
    //Only one can be > 0 e.g. Plastics has company_impact_minutes > 0, Assembly has plant_impact_minutes > 0
    if (configItem.company_impact_minutes > 0 && configItem.plant_impact_minutes > 0) {
      validationresult.isValid = false;
      validationresult.message = `Only one of 'Plant Impact (Minutes)' or 'Company Impact (Minutes)' may have a value > 0.`;
    }
    return Promise.resolve(validationresult);
  }

  async handleButtonClicked(event: ActionButtonEvent) {
    if (event.customAction === 'Config Line') {
      if (this.tableComponent?.isEditMode) {
        this.eventService.showSnackBar('A Shop Config item is being edited, please exit the edit mode !!', 'warn');
        return;
      }
      const selectedShop = event.entry;
      const dialogRef = this.dialog.open(ConfigurationComponent, {
        disableClose: true,
        maxWidth: '98vw',
        maxHeight: '98vh',
        height: '100%',
        width: '100%',
        data: {
          parentShop: selectedShop,
          configMode: ConfigMode.LINE,
          title: `Line Configuration for Shop : ${selectedShop.company_code} > ${selectedShop.plant_name} > ${selectedShop.shop_name}`,
          selectedFiscalYear: this.selectedFiscalYear,
          editableTable: this.editableTable
        },
        panelClass: 'custom-modalbox'
      });
      //Reload the shop config screen only if line config screen was updated by user
      dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async result => {
        if (result.shouldRefreshShop) {
          this.loader.message = 'Refreshing Shop Configuration...'
          await this.loadConfiguration(this.selectedFiscalYear.year);
        }
      });
    }
    else if (this.configMode === ConfigMode.SHOP && event.action === 'init' ) {
      await this.checkEditShopConfigColumns(event.entry);
    }
    else if (event.action === 'save') {
      const configItem = event.entry;
      //Allow calculation of downtime_cost as product only if both team_members and cost_per_minute are > 0
      if (configItem.team_members > 0 || configItem.cost_per_minute > 0) {
        configItem.downtime_cost = configItem.team_members * configItem.cost_per_minute;
        this.eventService.showSnackBar('Downtime Cost is calculated as product of Team Members and $/Minute', 'info');
      }
      //Whenever any input value is changed in line configuration, re-calculate the aggregates
      if (this.configMode === ConfigMode.LINE) {
        this.calculateLineAggregates();
      }
      this.itemsToSave.set(configItem.id, configItem);
      configItem.unsaved = true;
    }
  }

  async checkEditShopConfigColumns(shop: any) {
    //Initialize currentLineData if parent shop is different from currently stored line data
    if (this.currentLineData?.shop_id !== shop.shop_id) {
      this.currentLineData = { shop_id: shop.shop_id, lineItems: [] };
      this.setLoader(true, `Initializing Shop Configuration for ${shop.shop_name}...`, false);
      const response = await this.mfgappsApiService.getLineConfiguration(this.selectedFiscalYear.year, this.currentLineData.shop_id);
      if (Array.isArray(response['result'])) {
        this.currentLineData.lineItems = response['result'];
      }
      this.setLoader();
    }
    //Allow edit of 4 Shop config columns only if no lines present in shop or if any line has downtime_cost = 0
    let disableEdit = this.checkIfAllLinesHaveDowntimeCost(this.currentLineData?.lineItems);
    const columnsToCheck = ["team_members", "cost_per_minute", "downtime_cost", "takt_time_seconds"];
    for (const column of this.columnList) {
      if (columnsToCheck.includes(column.name)) {
        //Always disable edit 'takt_time_seconds' for Assembly type shops
        if (column.name === 'takt_time_seconds') {
          disableEdit = disableEdit || !this.eventService.checkIfShopHasVehicleAvoidance(shop);
        }
        column.inputType = disableEdit ? null : InputType.numeric;
      }
    }
  }

  checkIfAllLinesHaveDowntimeCost(lineItems: any[]) {
    return lineItems?.length > 0 && lineItems?.every(line => line.downtime_cost > 0);
  }
  
  calculateLineAggregates() {
    for (const field of this.aggregationFields) {
      let sum = 0;
      let weightedSum = 0;
      let weightedProduct = 0;
      for (const line of this.tableData) {
        //Sum the product of column value and weighting field value for weighted average calculation
        if (field.aggregate === 'WeightedAvg' && field.weightingField) {
          weightedProduct += line[field.name] * line[field.weightingField];
          weightedSum += line[field.weightingField];
        }
        else {
          sum += line[field.name];
        }
      }
      if (field.aggregate === 'WeightedAvg' && field.weightingField && weightedSum > 0) {
        field.value = +(weightedProduct / weightedSum).toFixed(field.floatDigits ?? 2);
      }
      else {
        const divisor = field.aggregate === 'Avg' && this.tableData.length ? this.tableData.length : 1;
        field.value = +(sum / divisor).toFixed(field.floatDigits ?? 2);
      }
    }
  }

  updateLineConfiguration() {
    //send shop_id, plant_id, company_id, fiscal_year (number) and array of modified line_configs
    const payload = {
      fiscal_year: this.selectedFiscalYear.year, shop_id: this.parentShop.shop_id, plant_id: this.parentShop.plant_id,
      company_id: this.parentShop.company_id, corporate_id: this.parentShop.corporate_id,
      line_configs: [...this.itemsToSave.values()]
    };
    if (this.shouldRefreshShop) {
      //send aggregation fields (Total, Avg, WeightedAvg etc.) for parent shop only if all lines have downtime_cost > 0.
      this.aggregationFields.forEach(field => payload[field.fieldToSave] = field.value);
    }
    return this.mfgappsApiService.updateLineConfiguration(payload);
  }

  updateShopConfiguration() {
    //send fiscal_year (year number) as common and shop_configs as array of modified shop_configs
    const payload = {
      shop_configs: [...this.itemsToSave.values()],
      fiscal_year: this.selectedFiscalYear.year
    };
    return this.mfgappsApiService.updateShopConfiguration(payload);
  }

  async onSubmit() {
    try {
      if (this.tableComponent?.isEditMode) {
        this.eventService.showSnackBar(`A ${this.configMode} Config item is being edited, please exit the edit mode !!`, 'warn');
      }
      else if (this.itemsToSave.size === 0) {
        this.eventService.showSnackBar(`No changes in ${this.configMode} Configuration items to save`, 'warn');
      }
      else {
        let confirmMsg = `Do you want to save all changes made in ${this.configMode} Configuration ?`;
        this.shouldRefreshShop = this.configMode === ConfigMode.LINE && this.checkIfAllLinesHaveDowntimeCost(this.tableData);
        //Add additional message that parent shop configuration will be updated based on line items if all lines have downtime_cost > 0
        if (this.shouldRefreshShop) {
          confirmMsg = 'The Parent Shop Configuration will also be updated based on aggregations from all Line Configuration items.\n'.concat(confirmMsg);
        }
        if (await this.eventService.confirmDialog(confirmMsg)) {
          this.setLoader(true, `Updating ${this.configMode} Configuration...`);
          await this.updateConfigurationMethod();
          this.eventService.showSnackBar(`${this.configMode} configuration updated successfully !`, 'success');
          await this.loadConfiguration(this.selectedFiscalYear.year);
        }
        else {
          //Reset the flag as user cancelled the update action on confirmation dialog
          this.shouldRefreshShop = false;
        }
      }
    }
    catch (error) {
      this.handleApiError(error, 'Updating');
    }
  }

  handleApiError(error: any, action: string, hideTable = false) {
    console.info(error);
    this.setLoader();
    let errorMessage = `Error occurred while ${action} ${this.configMode} Configuration: ${error.message}`;
    if (error.statusCode !== 403) {
      //Show message to try again only for non-403 (unauthorized/forbidden) errors
      errorMessage = errorMessage.concat(", Please try again !");
    }
    if (hideTable) {
      this.errorMessage = errorMessage;
    }
    this.eventService.showSnackBar(errorMessage, 'error');
  }

  async onCancel() {
    if (await this.eventService.confirmDialog('Any unsaved configuration changes will be lost. Do you wish to proceed?')) {
      await this.loadConfiguration(this.selectedFiscalYear.year);
    }
  }

  close() {
    this.dialogRef.close({ shouldRefreshShop: this.shouldRefreshShop });
  }

  setLoader(isLoading = false, message?: string, hideTable: boolean | null = null) {
    this.loader.isLoading = isLoading;
    this.loader.hideTable = hideTable ?? isLoading;
    this.loader.message = message ?? `Loading ${this.configMode} Configuration...`;
  }

  resetError() {
    this.errorMessage = '';
  }

  get pageTitle() {
    if (this.title) {
      return this.title;
    }
    return this.configMode === ConfigMode.LINE ? 'Manage Line Configuration' : 'Manage Shop Details';
  }

  get pageDescription() {
    if (this.dialogData?.isSummary) {
      return this.pageDescriptions.summary;
    }
    return this.configMode === ConfigMode.LINE ? this.pageDescriptions.line : this.pageDescriptions.shop;
  }

  get currentModeLabel() {
    if (this.dialogData?.isSummary) {
      return 'Read-only summary';
    }
    return this.editableTable ? 'Editable workspace' : 'Read-only workspace';
  }

  get currentModeIcon() {
    if (this.dialogData?.isSummary || !this.editableTable) {
      return 'lock';
    }
    return 'edit_note';
  }

  get primaryActionLabel() {
    return this.configMode === ConfigMode.LINE ? 'Save Line Configuration' : 'Save Shop Details';
  }
}